import {
  FontSizeEnum,
  FontWeightEnum,
  IconButton,
  IconButtonSizeEnum,
  IconSizeEnum,
  PauseIsolatedIcon,
  PlayIsolatedIcon,
  Typography,
} from '@acceso/design-system';
import { useTranslation } from 'react-i18next';

import { SPEED_OPTIONS } from '@/constants/speed-options';

import { FullscreenControl } from './FullscreenControl';
import VolumeControl from './VolumeControl';

type TCustomControlsProps = {
  isPlaying: boolean;
  played: number; // Fracción (0 - 1) para la barra de progreso
  duration: number; // Duración total en segundos
  volume: number; // Volumen (0 - 1)
  playbackRate: number;
  onPlayPause: () => void;
  onSeek: (value: number) => void;
  onVolumeChange: (value: number) => void;
  onPlaybackRateChange: (value: number) => void;
  onFullscreen: () => void;
  onChangeWidth: (value: '20%' | '50%' | '100%') => void;
  setIsControlsHovered: (value: boolean) => void;
  styles?: React.CSSProperties;
  markers?: { pos: number; type: 'start' | 'end' }[];
  reverseRate?: number | null;
};

export function CustomControls({
  isPlaying,
  played,
  duration,
  volume,
  playbackRate,
  markers,
  reverseRate,
  onChangeWidth,
  onPlayPause,
  onSeek,
  onVolumeChange,
  onPlaybackRateChange,
  onFullscreen,
  setIsControlsHovered,
}: TCustomControlsProps) {
  const { t } = useTranslation();
  const currentTimeSeconds = played * duration;
  const activeReverseRate = typeof reverseRate === 'number' && reverseRate < 0 ? reverseRate : null;

  const formatTime = (secs: number) => {
    if (!secs) return '0:00';
    const minutes = Math.floor(secs / 60);
    const seconds = Math.floor(secs % 60);
    return `${minutes}:${seconds < 10 ? '0' + seconds : seconds}`;
  };

  return (
    <div
      className="absolute bottom-5 flex w-full min-w-[350px] items-center gap-2 rounded-md bg-bg-primary-10 px-2 py-1"
      onMouseEnter={() => setIsControlsHovered(true)}
      onMouseLeave={() => setIsControlsHovered(false)}
    >
      <IconButton
        size={IconButtonSizeEnum.SM}
        aria-label={isPlaying ? t('Pausar') : t('Reproducir')}
        onClick={e => {
          e.stopPropagation();
          onPlayPause();
        }}
      >
        {isPlaying ? (
          <PauseIsolatedIcon size={IconSizeEnum.XS} />
        ) : (
          <PlayIsolatedIcon size={IconSizeEnum.XS} />
        )}
      </IconButton>

      <Typography
        className="!whitespace-nowrap"
        fontSize={FontSizeEnum.Xs}
        weight={FontWeightEnum.Semibold}
      >
        {formatTime(currentTimeSeconds)} / {formatTime(duration)}
      </Typography>

      {/* Barra de progreso + marcadores */}
      <div className="relative flex-1">
        <input
          type="range"
          min={0}
          max={1}
          step="0.01"
          value={played}
          data-video-progress-bar
          onMouseDown={e => e.stopPropagation()}
          onMouseUp={e => e.stopPropagation()}
          onClick={e => e.stopPropagation()}
          onChange={e => onSeek(parseFloat(e.target.value))}
          className="!hover:accent-accent-active-primary-40 w-full !accent-accent-active-primary-40"
        />

        {markers?.map(({ pos, type }, i) => (
          <div
            key={i}
            style={{ left: `${pos * 100}%` }}
            className={`pointer-events-none absolute bottom-0 top-0 w-[2px] -translate-y-[1.5px] ${
              type === 'start'
                ? 'translate-x-[6px] bg-feedback-success-30'
                : '-translate-x-[3.5px] bg-feedback-error-30'
            }`}
          />
        ))}
      </div>

      <select
        value={activeReverseRate ?? playbackRate}
        onChange={e => {
          const value = parseFloat(e.target.value);
          // The reverse entry is a read-out only; a negative rate would break the player.
          if (value > 0) {
            onPlaybackRateChange(value);
          }
        }}
        onMouseDown={e => e.stopPropagation()}
        onMouseUp={e => e.stopPropagation()}
        onClick={e => e.stopPropagation()}
        className="w-16 !border-b-[1px] border-stroke-30-1 !bg-white p-1 text-sm text-text-medium-10"
      >
        {activeReverseRate !== null && (
          <option value={activeReverseRate}>{`-${Math.abs(activeReverseRate)}x`}</option>
        )}
        {SPEED_OPTIONS.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>

      <VolumeControl onVolumeChange={onVolumeChange} volume={volume} />

      <FullscreenControl onFullscreen={onFullscreen} onChangeWidth={onChangeWidth} />
    </div>
  );
}
